"use client";

import { useAtomValue } from "@effect/atom-react";
import { useParams } from "@tanstack/react-router";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { OpenAddProjectCommandPaletteProvider } from "../commandPaletteContext";
import { ComposerHandleContext } from "../composerHandleContext";
import { isTerminalFocused } from "../lib/terminalFocus";
import { resolveShortcutCommand } from "../keybindings";
import { primaryServerKeybindingsAtom } from "../state/server";
import { useEnvironments } from "../state/environments";
import { useWorkspaceShellEntry } from "../state/workspaceShellEntry";
import { selectThreadTerminalUiState, useTerminalUiStateStore } from "../terminalUiStateStore";
import { resolveThreadRouteTarget } from "../threadRoutes";
import type { ChatComposerHandle } from "./chat/ChatComposer";
import type { CommandPaletteOpenIntent } from "./CommandPalette";
import { CommandDialog } from "./ui/command";

const CommandPaletteDialog = lazy(() =>
  import("./CommandPalette").then((module) => ({ default: module.CommandPaletteDialog })),
);

interface CommandPaletteUiState {
  readonly open: boolean;
  readonly openIntent: CommandPaletteOpenIntent | null;
}

type CommandPaletteUiAction =
  | { readonly _tag: "SetOpen"; readonly open: boolean }
  | { readonly _tag: "Toggle" }
  | { readonly _tag: "OpenAddProject" }
  | { readonly _tag: "ClearOpenIntent" };

function reduceCommandPaletteUiState(
  state: CommandPaletteUiState,
  action: CommandPaletteUiAction,
): CommandPaletteUiState {
  switch (action._tag) {
    case "SetOpen":
      return {
        open: action.open,
        openIntent: action.open ? state.openIntent : null,
      };
    case "Toggle":
      return { open: !state.open, openIntent: null };
    case "OpenAddProject":
      return { open: true, openIntent: { kind: "add-project" } };
    case "ClearOpenIntent":
      return state.openIntent ? { ...state, openIntent: null } : state;
  }
}

export function CommandPaletteShell({ children }: { readonly children: ReactNode }) {
  const [state, dispatch] = useReducer(reduceCommandPaletteUiState, {
    open: false,
    openIntent: null,
  });
  const [projectCreationActive, setProjectCreationActiveState] = useState(false);
  const projectCreationActiveRef = useRef(false);
  const setProjectCreationActive = useCallback((active: boolean) => {
    projectCreationActiveRef.current = active;
    setProjectCreationActiveState(active);
  }, []);
  const setOpen = useCallback((open: boolean) => {
    if (!open && projectCreationActiveRef.current) return;
    dispatch({ _tag: "SetOpen", open });
  }, []);
  const { environments } = useEnvironments();
  // The palette chooses an Environment per action; a disconnected parent must not block a ready guest.
  const shellReady = useWorkspaceShellEntry().kind === "ready";
  const isReady =
    shellReady && environments.some((environment) => environment.connection.phase === "ready");
  const toggleOpen = useCallback(() => {
    if (isReady && !projectCreationActiveRef.current) dispatch({ _tag: "Toggle" });
  }, [isReady]);
  const openAddProject = useCallback(() => {
    if (isReady) dispatch({ _tag: "OpenAddProject" });
  }, [isReady]);
  const clearOpenIntent = useCallback(() => dispatch({ _tag: "ClearOpenIntent" }), []);
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const composerHandleRef = useRef<ChatComposerHandle | null>(null);
  const routeTarget = useParams({
    strict: false,
    select: (params) => resolveThreadRouteTarget(params),
  });
  const routeThreadRef = routeTarget?.kind === "server" ? routeTarget.threadRef : null;
  const terminalOpen = useTerminalUiStateStore((store) =>
    routeThreadRef
      ? selectThreadTerminalUiState(store.terminalUiStateByThreadKey, routeThreadRef).terminalOpen
      : false,
  );

  useEffect(() => {
    if (!isReady && state.open && !projectCreationActive) {
      dispatch({ _tag: "SetOpen", open: false });
    }
  }, [projectCreationActive, isReady, state.open]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const command = resolveShortcutCommand(event, keybindings, {
        context: {
          terminalFocus: isTerminalFocused(),
          terminalOpen,
        },
      });
      if (command !== "commandPalette.toggle") return;
      event.preventDefault();
      event.stopPropagation();
      toggleOpen();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [keybindings, terminalOpen, toggleOpen]);

  return (
    <OpenAddProjectCommandPaletteProvider openAddProject={openAddProject}>
      <ComposerHandleContext value={composerHandleRef}>
        <CommandDialog open={state.open} onOpenChange={setOpen}>
          {children}
          {state.open ? (
            <Suspense fallback={null}>
              <CommandPaletteDialog
                open={state.open}
                openIntent={state.openIntent}
                setOpen={setOpen}
                clearOpenIntent={clearOpenIntent}
                setProjectCreationActive={setProjectCreationActive}
              />
            </Suspense>
          ) : null}
        </CommandDialog>
      </ComposerHandleContext>
    </OpenAddProjectCommandPaletteProvider>
  );
}
